import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCrossFlow } from '@atlassiansox/cross-flow-support';
import { isDesktop } from '@trello/browser';
import { useFeatureGate } from '@trello/feature-gate-client';

import type { TouchpointSourceType } from '../TouchpointSourceType';
import { trelloCrossFlowOpen } from '../TrelloCrossFlow';
import { useHasEligibleWorkspaces } from './runtimeEligibilityChecks/useHasEligibleWorkspaces';
import { useHasNoAvailableSites } from './runtimeEligibilityChecks/useHasNoAvailableSites';
import { useIsCrossFlowMemberConfirmed } from './runtimeEligibilityChecks/useIsCrossFlowMemberConfirmed';
import { useIsMultiplayerUser } from './runtimeEligibilityChecks/useIsMultiplayerUser';
import { useIsNotProductivityUserOrIsEligible } from './runtimeEligibilityChecks/useIsNotProductivityUserOrIsEligible';
import { useIsCrossFlowExperienceEligible } from './useIsCrossFlowExperienceEligible';

interface NonPpmMultiPlayerExperienceParams {
  source: TouchpointSourceType;
  workspaceId?: string;
  boardId?: string;
  hasValidAaSession: boolean;
}

const EXPERIENCE_KEY = 'freeExistingUserExperience';

export const useCrossFlowForNonPpmMultiPlayerExperience = ({
  source,
  workspaceId,
  boardId,
  hasValidAaSession,
}: NonPpmMultiPlayerExperienceParams) => {
  const crossFlow = useCrossFlow();

  // 1. Check that the member is confirmed
  const { isEligible: isMemberConfirmed } = useIsCrossFlowMemberConfirmed();
  // 2. Check that the member has eligible workspaces
  const { isEligible: hasEligibleWorkspaces, eligibleWorkspaceOptions } =
    useHasEligibleWorkspaces({
      workspaceId,
      entitlementRequired: 'free',
    });
  // 3. Check that the user is not on desktop
  const isWeb = !isDesktop();
  // 4. Check that the member is a multiplayer user
  const { isEligible: isMultiplayerUser } = useIsMultiplayerUser(boardId);
  // 5. Check that this member belongs in the cohort group
  const { value: isMultiPlayerCohortUser } = useFeatureGate(
    'trello_xf_non_ppm_multi_player_experience',
  );

  const [
    isImmediateRuntimeEligibilityChecksPassed,
    setImmediateRuntimeEligibilityChecksPassed,
  ] = useState(false);

  // 4. Check that the member has no available sites
  const { isEligible: hasNoAvailableSites } = useHasNoAvailableSites({
    doLoadAvailableSites: isImmediateRuntimeEligibilityChecksPassed,
  });
  // 5. Is not in personal productivity or passes the "ads for personal productivity" check
  const { isEligible: isPersonalProductivityEligible } =
    useIsNotProductivityUserOrIsEligible(source);

  const experience = useIsCrossFlowExperienceEligible({
    experience: EXPERIENCE_KEY,
    source,
    eligibilityCheckResults: [
      crossFlow.isEnabled,
      Boolean(isMultiPlayerCohortUser),
      hasEligibleWorkspaces,
      isMemberConfirmed,
      isWeb,
      isMultiplayerUser,
      isPersonalProductivityEligible,
    ],
    deferredEligibilityCheckResults: [hasNoAvailableSites],
  });

  useEffect(() => {
    setImmediateRuntimeEligibilityChecksPassed(
      experience.isImmediateRuntimeEligibilityChecksPassed,
    );
  }, [experience.isImmediateRuntimeEligibilityChecksPassed]);

  const getIsTargeted = useCallback(() => {
    return isMultiPlayerCohortUser;
  }, [isMultiPlayerCohortUser]);

  return useMemo(
    () => ({
      experience: experience.experience,
      isEligible: experience.isEligible,
      open: trelloCrossFlowOpen(
        crossFlow,
        source,
        eligibleWorkspaceOptions,
        hasValidAaSession,
        {
          workspaceId,
          boardId,
          sporkPositioning: 'customization',
        },
      ),
      getIsTargeted,
    }),
    [
      experience.experience,
      experience.isEligible,
      crossFlow,
      source,
      eligibleWorkspaceOptions,
      hasValidAaSession,
      workspaceId,
      boardId,
      getIsTargeted,
    ],
  );
};
